import { BROKER_ACTIVACION } from "../constants/permisos";
import { ROLES } from "../constants/roles";

/**
 * Resuelve el nombre a mostrar para el ejecutivo de una venta.
 *
 * Lógica (en orden):
 *  1. Si el ejecutivo es un usuario rol 'broker' → siempre el intermediario broker
 *     (ej. "ADLAB"): la venta fue creada/atribuida a un broker.
 *  2. Si el CLIENTE tiene `broker_id` Y la venta cae en el período broker
 *     (no está muerta pre-activación ni cerrada antes de la activación)
 *     → intermediario broker del cliente. Cubre cuentas cedidas.
 *  3. Fallback: nombre del usuario ejecutivo. Cubre las pre-período.
 *
 * Comparación por ID numérico (regla 8): nunca por nombre.
 *
 * @param {number|string} ejecutivoId - id del usuario ejecutivo (sales.ejecutivo)
 * @param {Array} usuarios - lista de usuarios (con id, nombre, rol, agencia)
 * @param {Array} intermediarios - lista de intermediarios (con id, nombre)
 * @param {number|string|null} [clientId] - client_id de la venta (opcional; retrocompat)
 * @param {Array|Object|null} [clientes] - clientes como array o map {id: cliente} (opcional)
 * @param {string|null} [createdAt] - created_at de la venta (opcional)
 * @param {string|null} [estatus] - estatus de la venta (opcional)
 * @param {string|null} [fechaCierre] - fecha_cierre de la venta (opcional)
 * @param {boolean} [excluirBroker] - sales.excluir_broker: si true, la venta NUNCA se atribuye al
 *   broker (se trata como no-período-broker → titular real), sin importar broker_id/estatus/fecha.
 * @returns {string|null} nombre a mostrar, o null si no se encuentra el usuario
 *   (el caller aplica su propio fallback).
 */
export function resolverNombreEjecutivo(
  ejecutivoId,
  usuarios = [],
  intermediarios = [],
  clientId = null,
  clientes = null,
  createdAt = null,
  estatus = null,
  fechaCierre = null,
  excluirBroker = false
) {
  const userEjecutivo = usuarios.find((u) => String(u.id) === String(ejecutivoId));

  // Paso 1: el ejecutivo es un broker → siempre el intermediario broker.
  if (userEjecutivo?.rol === ROLES.BROKER) {
    const broker = intermediarios.find((i) => String(i.id) === String(userEjecutivo.agencia));
    return broker?.nombre || userEjecutivo.nombre;
  }

  // Paso 2: cliente con broker_id Y la venta cae en el período broker.
  if (clientId != null && clientes) {
    const cliente = Array.isArray(clientes)
      ? clientes.find((c) => String(c.id) === String(clientId))
      : clientes[clientId];
    if (cliente?.broker_id) {
      // Una venta de cuenta cedida NO es del período broker solo si está muerta antes
      // del período (0% creada pre-activación) o cerrada antes del período (100%). Para
      // 100% se usa fecha_cierre; si es NULL (históricas pre-trigger 2024/25) se cae a
      // created_at. Todo lo demás (viva, o cerrada desde la activación) → broker.
      const brokerActivacion = new Date(BROKER_ACTIVACION);
      const noEsPeriodoBroker =
        // Exclusión POR-VENTA (reasignación Perú): fuerza titular real, nunca ADLAB (invariante: default false).
        excluirBroker === true ||
        (estatus === "0%" && createdAt && new Date(createdAt) < brokerActivacion) ||
        (estatus === "100%" &&
          ((fechaCierre && new Date(fechaCierre) < brokerActivacion) ||
            (!fechaCierre && createdAt && new Date(createdAt) < brokerActivacion)));
      if (!noEsPeriodoBroker) {
        const broker = intermediarios.find((i) => String(i.id) === String(cliente.broker_id));
        if (broker) return broker.nombre;
      }
    }
  }

  // Paso 3: fallback — nombre del ejecutivo.
  return userEjecutivo?.nombre || null;
}
